import React, { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  Box,
  Typography,
  styled,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  usePublicTestSuiteResultQuery,
  usePublicTestCaseResultsFromTestSuiteQuery,
  usePublicErrorCodesQuery,
  usePublicStatusPatternQuery,
  usePublicTestSuitesQuery,
  usePublicProductsQuery,
} from "../../useQuery/usePublicQuery";
import { useGetMileStoneTableQuery } from "../../useQuery";
import PrettyDonut from "../Reports/PrettyDonut";
import ErrorCodeDonut from "../Reports/ErrorCodesDonut";
import { PatternBarGraph, PatternDetailsTable } from "../Reports/PatternStatusComponents";
import { IssueIndicator } from "../../components-new/common";
import dayjs from "dayjs";
import { Videocam } from "@mui/icons-material";
import { ExeReportHistoryStatus } from "../../components-new/layout/constants";
import { getUserDetails } from "../../utils/userDetailsUtils";
const StyledTableCell = styled(Box)`
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  background: #2550d1;
  border-bottom: 1px solid #e2e6f3;
`;

const StyledInfoBox = styled(Box)`
  padding: 16px 20px;
`;

const StyledTypography = styled(Typography)`
  font-size: 14px;
  font-weight: 600;
  color: #232d3d;
`;

const StyledColumnTitle = styled(StyledTableCell)`
  text-align: center;
  padding: 12px 10px;
  flex: 1;
  &:not(:last-child) {
    border-right: 2px solid rgba(226, 230, 243, 0.8);
  }
`;

const StyledColumnValue = styled(Box)`
  text-align: center;
  padding: 12px 10px;
  flex: 1;
  color: #2b3539;
  font-size: 16px;
  font-weight: 500;
  &:not(:last-child) {
    border-right: 2px solid rgba(226, 230, 243, 0.8);
  }
`;
const StyledTitleLeft = styled(Box)`
  flex: 2;
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  border-right: 2px solid rgba(226, 230, 243, 0.8);
`;

const StyledTitleRight = styled(Box)`
  flex: 1;
  color: #e0e0e0;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  text-align: center;
`;

const StyledInfoLeft = styled(Box)`
  flex: 2;
  color: #232d3d;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  border-right: 2px solid rgba(226, 230, 243, 0.8);
`;

const StyledInfoRight = styled(Box)`
  flex: 1;
  color: #232d3d;
  font-size: 14px;
  font-weight: 600;
  padding: 16px 20px;
  text-align: center;
`;
const PublicsuiteResults = () => {
  const { pcid, tsid, tsrid, companyId, otp } = useParams();
  const [searchParams] = useSearchParams();
  const detailed = searchParams.get("detailed");
  let userDetails = null;
  try {
    userDetails = getUserDetails();
  } catch {
    userDetails = null;
  }
  const showDetailedSuiteReport = detailed === "0";
  const [testSuiteData, setTestSuiteData] = useState(null);

  const { data: testSuiteResults, isLoading } = usePublicTestSuiteResultQuery(
    tsid,
    tsrid,
    companyId,
    otp,
    {
      enabled: !!tsid && !!companyId && !!otp,
    },
  );

  const { data: testCaseData } = usePublicTestCaseResultsFromTestSuiteQuery(
    tsrid,
    "All",
    companyId,
    otp,
    {
      enabled: !!tsrid && !!companyId && !!otp,
    },
  );

  const { data: errorCodesData } = usePublicErrorCodesQuery(tsrid, companyId, otp, {
    enabled: !!tsrid && !!companyId && !!otp,
  });

  const { data: patternData } = usePublicStatusPatternQuery(tsrid, companyId, otp, {
    enabled: !!tsrid && !!companyId && !!otp,
  });

  const {
    data: mileStoneData,
    isLoading: isMileStoneLoading,
    isError: isMileStoneError,
  } = useGetMileStoneTableQuery(tsrid, {
    enabled: !showDetailedSuiteReport && !!tsrid && !!userDetails?.email,
  });

  const { data: suiteDetailsData } = usePublicTestSuitesQuery(pcid, companyId, otp, {
    enabled: !!pcid && !!companyId && !!otp,
  });

  const { data: productsData } = usePublicProductsQuery(companyId, otp, {
    enabled: !!companyId && !!otp,
  });

  const matchedSuite = suiteDetailsData
    ?.flat()
    ?.find((s) => String(s.idtest_suite) === String(tsid));

  const matchedProduct = productsData?.find((p) => String(p.idproducts) === String(pcid));

  useEffect(() => {
    if (testSuiteResults?.length > 0) {
      const latestResult = testSuiteResults[0];
      setTestSuiteData(latestResult);
    }
  }, [testSuiteResults]);
  const getErrorCodesForDisplay = () => {
    if (!errorCodesData || !errorCodesData.error_codes) return [];
    return Object.entries(errorCodesData.error_codes).map(([code, details]) => ({
      code,
      message: details.message,
      count: details.failedTestCasesCount,
      firstRunCount: details.firstRunCount || 0,
      rerunCount: details.rerunCount || 0,
    }));
  };
  const errorCodes = getErrorCodesForDisplay();

  if (isLoading) {
    return (
      <Box p={3}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  if (!testSuiteData) {
    return (
      <Box p={3}>
        <Typography>No test suite results found</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: "1400px", margin: "0 auto" }}>
      {/* Product / Session / Build header */}
      <Box
        mt={2}
        display="flex"
        bgcolor={"#fff"}
        borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}
        sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
      >
        <StyledInfoBox flex={1}>
          <StyledTypography>Product: {matchedProduct?.productname || "—"}</StyledTypography>
        </StyledInfoBox>
        <StyledInfoBox
          flex={1}
          borderRight={`2px solid rgba(226, 230, 243, 0.80)`}
          borderLeft={`2px solid rgba(226, 230, 243, 0.80)`}
        >
          <StyledTypography>Session:</StyledTypography>
        </StyledInfoBox>
        <StyledInfoBox flex={1}>
          <StyledTypography>Build: {testSuiteData.build || "#"}</StyledTypography>
        </StyledInfoBox>
      </Box>

      {/* Execution Information title bar */}
      <Box
        mt={4}
        bgcolor={"#2550D1"}
        sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
      >
        <Typography color="#E0E0E0" fontWeight={600} padding="16px 20px">
          Execution Information
        </Typography>
      </Box>
      {/* Execution Summary */}
      <Box display="flex" gap={2}>
        <Box
          mt={2}
          flex={1}
          bgcolor={"#fff"}
          sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
        >
          <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
            <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledTypography>Suite Name</StyledTypography>
            </StyledInfoBox>
            <StyledInfoBox flex={2}>
              <StyledTypography>{testSuiteData.testsuitename}</StyledTypography>
            </StyledInfoBox>
          </Box>
          <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
            <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledTypography>Session Start Time</StyledTypography>
            </StyledInfoBox>
            <StyledInfoBox flex={2}>
              <StyledTypography>
                {dayjs(testSuiteData.Executed_Date).format("DD/MM/YYYY HH:mm:ss")}
              </StyledTypography>
            </StyledInfoBox>
          </Box>
          <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
            <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledTypography>Execution Time</StyledTypography>
            </StyledInfoBox>
            <StyledInfoBox flex={2}>
              <StyledTypography>{testSuiteData.Duration}</StyledTypography>
            </StyledInfoBox>
          </Box>
        </Box>
        <Box
          mt={2}
          flex={1}
          bgcolor={"#fff"}
          sx={{ borderTopRightRadius: "8px", borderTopLeftRadius: "8px" }}
        >
          <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
            <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledTypography>No. of Test Cases</StyledTypography>
            </StyledInfoBox>
            <StyledInfoBox flex={2}>
              <StyledTypography>{testSuiteData.totalCount}</StyledTypography>
            </StyledInfoBox>
          </Box>
          <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
            <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledTypography>Session End Time</StyledTypography>
            </StyledInfoBox>
            <StyledInfoBox flex={2}>
              <StyledTypography>
                {dayjs(testSuiteData.Executed_Date)
                  .add(testSuiteData.Duration, "second")
                  .format("DD/MM/YYYY HH:mm:ss")}
              </StyledTypography>
            </StyledInfoBox>
          </Box>
          <Box display="flex" borderBottom={`2px solid rgba(226, 230, 243, 0.80)`}>
            <StyledInfoBox flex={1} borderRight={`2px solid rgba(226, 230, 243, 0.80)`}>
              <StyledTypography>Execution Status</StyledTypography>
            </StyledInfoBox>
            <StyledInfoBox flex={2}>
              <IssueIndicator
                status={testSuiteData.Status}
                issues={testSuiteData.issues}
                issueType={testSuiteData.issueType}
                showStatusText={true}
              />
            </StyledInfoBox>
          </Box>
        </Box>
      </Box>

      <Box display="flex" mt={4} gap={2}>
        <Box flex={1}>
          <PrettyDonut data={testSuiteData} simplified={!showDetailedSuiteReport} />
        </Box>

        {showDetailedSuiteReport && (
          <Box flex={1}>{errorCodesData && <ErrorCodeDonut data={errorCodesData} />}</Box>
        )}

        {showDetailedSuiteReport && (
          <Box flex={2}>{patternData && <PatternBarGraph patterns={patternData.patterns} />}</Box>
        )}
      </Box>

      <Box mt={4}>
        <Box
          bgcolor={"#2550D1"}
          sx={{
            borderTopLeftRadius: "8px",
            borderTopRightRadius: "8px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Typography
            color="#E0E0E0"
            fontWeight={600}
            padding="16px 20px"
            borderBottom={1}
            borderColor={"gray"}
          >
            Test Suite Execution Summary
          </Typography>
        </Box>

        <Box display="flex" bgcolor={"#2550D1"}>
          <StyledColumnTitle>Total Test Cases</StyledColumnTitle>
          <StyledColumnTitle sx={{ backgroundColor: "#10B981" }}>Pass</StyledColumnTitle>
          <StyledColumnTitle sx={{ backgroundColor: "#EF4444" }}>Fail</StyledColumnTitle>
          <StyledColumnTitle>Not Executed</StyledColumnTitle>
          <StyledColumnTitle>Skipped Over</StyledColumnTitle>
          <StyledColumnTitle sx={{ backgroundColor: "#10B981" }}>First Run Pass</StyledColumnTitle>
          <StyledColumnTitle sx={{ backgroundColor: "#EF4444" }}>First Run Fail</StyledColumnTitle>
          <StyledColumnTitle sx={{ backgroundColor: "#10B981" }}>Second Run Pass</StyledColumnTitle>
          <StyledColumnTitle sx={{ backgroundColor: "#EF4444" }}>Second Run Fail</StyledColumnTitle>
        </Box>

        <Box display="flex" bgcolor={"#ffffff"}>
          <StyledColumnValue>{testSuiteData.totalCount}</StyledColumnValue>
          <StyledColumnValue>{testSuiteData.passCount}</StyledColumnValue>
          <StyledColumnValue>{testSuiteData.failCount}</StyledColumnValue>
          <StyledColumnValue>
            {testSuiteData.totalCount - (testSuiteData.passCount + testSuiteData.failCount)}
          </StyledColumnValue>
          <StyledColumnValue>{testSuiteData?.skippedOver || "0"}</StyledColumnValue>
          <StyledColumnValue>{testSuiteData?.firstRunPassCount || "0"}</StyledColumnValue>
          <StyledColumnValue>{testSuiteData?.firstRunFailCount || "0"}</StyledColumnValue>
          <StyledColumnValue>{testSuiteData?.secondRunPassCount || "0"}</StyledColumnValue>
          <StyledColumnValue>{testSuiteData?.secondRunFailCount || "0"}</StyledColumnValue>
        </Box>
      </Box>
      {/* Error Codes Table */}
      {showDetailedSuiteReport && (
        <Box
          flex={1}
          overflow="hidden"
          mt={4}
          sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
        >
          <TableContainer component={Box}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#2550D1" }}>
                  <TableCell
                    sx={{
                      color: "#E0E0E0",
                      fontWeight: 600,
                      borderBottom: "1px solid #e2e6f3",
                      width: "25%",
                      borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                    }}
                  >
                    Error Code
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#E0E0E0",
                      fontWeight: 600,
                      borderBottom: "1px solid #e2e6f3",
                      width: "8%",
                      textAlign: "center",
                      borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                    }}
                  >
                    <Tooltip title="First Run / Second Run Count" placement="top" arrow>
                      <span>First/Second</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#E0E0E0",
                      fontWeight: 600,
                      borderBottom: "1px solid #e2e6f3",
                      width: "25%",
                      borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                    }}
                  >
                    Error Code
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#E0E0E0",
                      fontWeight: 600,
                      borderBottom: "1px solid #e2e6f3",
                      width: "8%",
                      textAlign: "center",
                      borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                    }}
                  >
                    <Tooltip title="First Run / Second Run Count" placement="top" arrow>
                      <span>First/Second</span>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#E0E0E0",
                      fontWeight: 600,
                      borderBottom: "1px solid #e2e6f3",
                      width: "25%",
                      borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                    }}
                  >
                    Error Code
                  </TableCell>
                  <TableCell
                    sx={{
                      color: "#E0E0E0",
                      fontWeight: 600,
                      borderBottom: "1px solid #e2e6f3",
                      width: "8%",
                      textAlign: "center",
                    }}
                  >
                    <Tooltip title="First Run / Second Run Count" placement="top" arrow>
                      <span>First/Second</span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {errorCodes.length > 0 ? (
                  Array.from({ length: Math.ceil(errorCodes.length / 3) }).map((_, i) => (
                    <TableRow key={i} sx={{ backgroundColor: "#ffffff" }}>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        {errorCodes[i * 3] ? (
                          <>
                            <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                              {errorCodes[i * 3].code}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" fontSize="12px">
                              {errorCodes[i * 3].message}
                            </Typography>
                          </>
                        ) : null}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          textAlign: "center",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        {errorCodes[i * 3] ? (
                          <Typography
                            color="#232D3D"
                            fontWeight={600}
                            fontSize="14px"
                          >{`${errorCodes[i * 3].firstRunCount}/${errorCodes[i * 3].rerunCount}`}</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        {errorCodes[i * 3 + 1] ? (
                          <>
                            <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                              {errorCodes[i * 3 + 1].code}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" fontSize="12px">
                              {errorCodes[i * 3 + 1].message}
                            </Typography>
                          </>
                        ) : null}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          textAlign: "center",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        {errorCodes[i * 3 + 1] ? (
                          <Typography
                            color="#232D3D"
                            fontWeight={600}
                            fontSize="14px"
                          >{`${errorCodes[i * 3 + 1].firstRunCount}/${errorCodes[i * 3 + 1].rerunCount}`}</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                        }}
                      >
                        {errorCodes[i * 3 + 2] ? (
                          <>
                            <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                              {errorCodes[i * 3 + 2].code}
                            </Typography>
                            <Typography variant="body2" color="textSecondary" fontSize="12px">
                              {errorCodes[i * 3 + 2].message}
                            </Typography>
                          </>
                        ) : null}
                      </TableCell>
                      <TableCell
                        sx={{
                          padding: "16px 20px",
                          borderBottom: "1px solid #eee",
                          textAlign: "center",
                        }}
                      >
                        {errorCodes[i * 3 + 2] ? (
                          <Typography
                            color="#232D3D"
                            fontWeight={600}
                            fontSize="14px"
                          >{`${errorCodes[i * 3 + 2].firstRunCount}/${errorCodes[i * 3 + 2].rerunCount}`}</Typography>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow sx={{ backgroundColor: "#ffffff" }}>
                    <TableCell
                      sx={{
                        padding: "16px 20px",
                        borderBottom: "1px solid #eee",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                        No error codes available
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px 20px",
                        borderBottom: "1px solid #eee",
                        textAlign: "center",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      <Typography color="#232D3D" fontWeight={600} fontSize="14px">
                        0/0
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px 20px",
                        borderBottom: "1px solid #eee",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      No error codes available
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px 20px",
                        borderBottom: "1px solid #eee",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      0/0
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "16px 20px",
                        borderBottom: "1px solid #eee",
                        borderRight: "1px solid rgba(226, 230, 243, 0.8)",
                      }}
                    >
                      No error codes available
                    </TableCell>
                    <TableCell sx={{ padding: "16px 20px", borderBottom: "1px solid #eee" }}>
                      0/0
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
      <Box display="flex" mt={4} gap={2} pb={5}>
        {showDetailedSuiteReport && (
          <Box
            flex={1}
            overflow="hidden"
            sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}
          >
            <Box display="flex" bgcolor={"#2550D1"}>
              <StyledTitleLeft>Execution Settings</StyledTitleLeft>
              <StyledTitleRight>Value</StyledTitleRight>
            </Box>
            <Box display="flex" bgcolor={"#ffffff"}>
              <StyledInfoLeft>Browser</StyledInfoLeft>
              <StyledInfoRight>{(matchedSuite?.browser || "0").toString()}</StyledInfoRight>
            </Box>
            <Box display="flex" bgcolor={"#ffffff"}>
              <StyledInfoLeft>Env</StyledInfoLeft>
              <StyledInfoRight sx={{ overflowWrap: "anywhere" }}>
                {(matchedSuite?.envurl || "NA").toString()}
              </StyledInfoRight>
            </Box>
            <Box display="flex" bgcolor={"#ffffff"}>
              <StyledInfoLeft>Stop test after fail</StyledInfoLeft>
              <StyledInfoRight>
                {(matchedSuite?.stopafterfailure || "NA").toString().toUpperCase()}
              </StyledInfoRight>
            </Box>
            <Box display="flex" bgcolor={"#ffffff"}>
              <StyledInfoLeft>Rerun fail test</StyledInfoLeft>
              <StyledInfoRight>
                {(matchedSuite?.rerunfailedtests || "NA").toString().toUpperCase()}
              </StyledInfoRight>
            </Box>
            <Box display="flex" bgcolor={"#ffffff"}>
              <StyledInfoLeft>Run Tests in Parallel</StyledInfoLeft>
              <StyledInfoRight>
                {(matchedSuite?.supports_parallel_execution || "NA").toString().toUpperCase()}
              </StyledInfoRight>
            </Box>
            <Box display="flex" bgcolor={"#ffffff"}>
              <StyledInfoLeft>Synchronized Tests</StyledInfoLeft>
              <StyledInfoRight>
                {String(matchedSuite?.synchedscenarios ?? "").toUpperCase()}
              </StyledInfoRight>
            </Box>
            <Box display="flex" bgcolor={"#ffffff"}>
              <StyledInfoLeft>Headless Execution</StyledInfoLeft>
              <StyledInfoRight>
                {String(matchedSuite?.headless ?? "NA").toUpperCase()}
              </StyledInfoRight>
            </Box>
            <Box display="flex" bgcolor={"#ffffff"}>
              <StyledInfoLeft>Screenshots on Failure</StyledInfoLeft>
              <StyledInfoRight>
                {String(matchedSuite?.ssonerror ?? "").toUpperCase()}
              </StyledInfoRight>
            </Box>
            <Box display="flex" bgcolor={"#ffffff"}>
              <StyledInfoLeft>Send Individual Result Email</StyledInfoLeft>
              <StyledInfoRight>
                {(matchedSuite?.resultsemail ?? false).toString().toUpperCase()}
              </StyledInfoRight>
            </Box>
          </Box>
        )}
        <Box flex={1}>
          {showDetailedSuiteReport && patternData && (
            <PatternDetailsTable patterns={patternData.patterns} />
          )}
        </Box>
      </Box>

      <Box mt={4}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ color: "#e0e0e0", fontWeight: 600, background: "#2550d1" }}>
                  Run Date
                </TableCell>
                <TableCell sx={{ color: "#e0e0e0", fontWeight: 600, background: "#2550d1" }}>
                  Run By
                </TableCell>
                <TableCell sx={{ color: "#e0e0e0", fontWeight: 600, background: "#2550d1" }}>
                  Run Duration
                </TableCell>
                <TableCell sx={{ color: "#e0e0e0", fontWeight: 600, background: "#2550d1" }}>
                  Log File
                </TableCell>
                <TableCell sx={{ color: "#e0e0e0", fontWeight: 600, background: "#2550d1" }}>
                  Browser
                </TableCell>
                <TableCell sx={{ color: "#e0e0e0", fontWeight: 600, background: "#2550d1" }}>
                  Status
                </TableCell>
                <TableCell sx={{ color: "#e0e0e0", fontWeight: 600, background: "#2550d1" }}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {testCaseData?.testcaseresult?.length ? (
                testCaseData.testcaseresult.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.Executed_Date}</TableCell>
                    <TableCell>{item.Executed_By}</TableCell>
                    <TableCell>{item.Duration} s</TableCell>
                    <TableCell>
                      <a
                        target="_blank"
                        href={item.logfile}
                        rel="noopener noreferrer"
                        style={{ color: "#2550d1", textDecoration: "underline" }}
                      >
                        {item.testcase}
                      </a>
                    </TableCell>
                    <TableCell>{item.Browser}</TableCell>
                    <TableCell>
                      <Box
                        sx={{
                          display: "inline-block",
                          minWidth: "82px",
                          padding: "8px 10px",
                          borderRadius: "41px",
                          backgroundColor:
                            item.Status === "PASS"
                              ? "#e7fbe4"
                              : item.Status === "FAIL"
                                ? "#fbe7e7"
                                : "#D3D3D3",
                          textAlign: "center",
                          textTransform: "capitalize",
                        }}
                      >
                        {ExeReportHistoryStatus[item.Status] || item.Status}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {item.wasVideoRecorded && (
                        <Tooltip title="View Execution Recording" arrow>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => window.open(item.videofileurl, "_blank")}
                          >
                            <Videocam fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell align="center" colSpan={7}>
                    No data available
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
      {!showDetailedSuiteReport && mileStoneData?.length > 0 && (
        <Box mt={4}>
          <Box bgcolor={"#2550D1"} sx={{ borderTopLeftRadius: "8px", borderTopRightRadius: "8px" }}>
            <Typography color="#E0E0E0" fontWeight={600} padding="16px 20px">
              Validations by Test Case
            </Typography>
          </Box>
          <TableContainer component={Box}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: "#2550D1" }}>
                  <TableCell sx={{ color: "#E0E0E0", fontWeight: 600, background: "#2550d1" }}>
                    #
                  </TableCell>
                  <TableCell sx={{ color: "#E0E0E0", fontWeight: 600, background: "#2550d1" }}>
                    Test Case
                  </TableCell>
                  <TableCell sx={{ color: "#E0E0E0", fontWeight: 600, background: "#2550d1" }}>
                    Module Name
                  </TableCell>
                  <TableCell sx={{ color: "#E0E0E0", fontWeight: 600, background: "#2550d1" }}>
                    Total Validations
                  </TableCell>
                  <TableCell sx={{ color: "#E0E0E0", fontWeight: 600, background: "#2550d1" }}>
                    Validations Passed
                  </TableCell>
                  <TableCell sx={{ color: "#E0E0E0", fontWeight: 600, background: "#2550d1" }}>
                    Validations Failed
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {mileStoneData.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{index + 1}</TableCell>
                    <TableCell>{item["Test Case"]}</TableCell>
                    <TableCell>{item["Module Name"]}</TableCell>
                    <TableCell>{item["Total Validations"]}</TableCell>
                    <TableCell>{item["Validations Passed"]}</TableCell>
                    <TableCell>{item["Validations Failed"]}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}
    </Box>
  );
};

export default PublicsuiteResults;
